/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2021, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */
/* eslint-disable react/no-unstable-nested-components */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'contexts/config';
import { useApi } from 'contexts/ApiHandler';
import { Switch, Route, Redirect, useParams, useHistory } from 'react-router-dom';
import { apiPath, decodeChannelName } from 'utils/navigation';
import { getAPIType } from 'utils/APITypes';
import Operation from './Operation';
import Overview from './Overview';
import AsyncApiOperationOverview from './AsyncApiOperationOverview';
import AsyncApiOperationDetail from './AsyncApiOperationDetail';
import Webhook from './Webhook';
import Definitions from './Definitions';
import DocumentationPage from './DocumentationPage';
import GraphQLDisplay from './GraphQLDisplay';

// This disables the react-hooks/rules-of-hooks linting check because it is incorrectly flagging issues using
// use* hooks inside callbacks. They are actually being used correctly, in components defined under the Routes
// but the linter isn't able to determine this.
/* eslint react-hooks/rules-of-hooks: 0 */

const getDocumentation = (docs, docName) => {
  if (docName.includes('/')) {
    const splitDocName = docName.split('/');
    const parent = getDocumentation(docs, splitDocName.shift());
    docName = splitDocName.join('/');
    return getDocumentation(parent.docs, docName);
  }
  return docs.find((doc) => doc.name === docName);
};

const getDefaultRoute = (docsActive, docs, apis) => (docsActive ? `/documentation/${docs[0].name}` : `/${apiPath(apis[0])}/overview`);

function Routing({ apiState }) {
  const { docs, apis, apps, options } = useConfig();
  const { api, apiHandler } = useApi();
  const docsActive = Array.isArray(docs) && docs.length > 0;
  const [defaultRoute, updateDefaultRoute] = useState(() => getDefaultRoute(docsActive, docs, apis));

  const { t } = useTranslation();
  const history = useHistory();

  useEffect(() => {
    const newRoute = getDefaultRoute(docsActive, docs, apis);
    if (newRoute !== defaultRoute) {
      updateDefaultRoute(newRoute);
      // if the defaultRoute changes, we need to redirect incase the current api doesn't exist anymore
      history.push(newRoute);
    }
  }, [apis, docs, docsActive, defaultRoute, history]);

  return (
    <Switch>
      <Route exact path={['/:api/overview', '/:api/try-it']}>
        {getAPIType(api) === 'GraphQL' ? (
          <GraphQLDisplay
            options={options}
            api={api}
            infoSectionEntries={apiState.infoSectionEntries}
            documentationEntries={apiState.documentationEntries}
            apps={apps}
          />
        ) : (
          <Overview infoSectionEntries={apiState.infoSectionEntries} documentationEntries={apiState.documentationEntries} />
        )}
      </Route>
      <Route
        path="/:api/operation/:path/:method"
        component={() => {
          const path = useParams();
          const operationPath = decodeURIComponent(path.path);

          return (
            <Operation
              operation={apiState.currentApi.paths[operationPath][path.method]}
              operationPath={operationPath}
              operationMethod={path.method}
              apps={apps}
              data-testid="operation"
              options={options}
            />
          );
        }}
      />
      <Route
        path="/:api/webhook/:event"
        component={() => {
          const { event } = useParams();
          return <Webhook api={api} event={event} />;
        }}
      />
      <Route path="/:api/definitions">
        <Definitions options={options} api={api} infoSectionEntries={apiState.infoSectionEntries} />
      </Route>
      <Route
        path="/:api/:channel/overview"
        component={() => {
          const path = useParams();
          const channel = decodeChannelName(path.channel);
          return <AsyncApiOperationOverview channelName={channel} />;
        }}
      />
      <Route
        path="/:api/:channel/operation/:operationId"
        component={() => {
          const path = useParams();
          const channel = decodeChannelName(path.channel);
          const operationId = decodeURIComponent(path.operationId);
          const channelType = apiHandler.api.operations().get(operationId).action();
          return <AsyncApiOperationDetail channelName={channel} channelType={channelType} operationId={operationId} />;
        }}
      />
      <Route
        path="/:api/:channel/send"
        component={() => {
          const path = useParams();
          const channel = decodeChannelName(path.channel);
          return <AsyncApiOperationDetail channelName={channel} channelType="publish" />;
        }}
      />
      <Route
        path="/:api/:channel/subscribe"
        component={() => {
          const path = useParams();
          const channel = decodeChannelName(path.channel);
          return <AsyncApiOperationDetail channelName={channel} channelType="subscribe" />;
        }}
      />
      <Route
        path="/:api/:channel/publish"
        component={() => {
          const path = useParams();
          const channel = decodeChannelName(path.channel);
          return <AsyncApiOperationDetail channelName={channel} channelType="publish" />;
        }}
      />
      <Route
        path="/documentation/:docName+"
        component={() => {
          const path = useParams();
          const doc = getDocumentation(docs, path.docName);

          return <DocumentationPage options={options} apis={apis} doc={doc} />;
        }}
      />
      <Route
        path="/:api/documentation/:docName+"
        component={() => {
          const path = useParams();
          const doc = getDocumentation(api['x-embedded-doc'], path.docName);

          return <DocumentationPage options={options} apis={apis} doc={doc} />;
        }}
      />
      <Redirect from="/" to={defaultRoute} exact />
      <Route component={() => <div data-testid="404Error">{t('error_404')}</div>} />
    </Switch>
  );
}

export default Routing;
